import {
  Ban,
  BookMarked,
  BookOpen,
  CalendarCheck,
  CalendarDays,
  CalendarRange,
  CircleAlert,
  CircleCheck,
  Clock,
  DoorOpen,
  History,
  Hourglass,
  Lock,
  Megaphone,
  Plus,
  Settings,
  ShieldCheck,
  Trash2,
  UserRound,
  Users,
} from "lucide-react";
import {
  gerenciaElo,
  type IlustracaoEloId,
  type PapelAjuda,
} from "@/core/ajuda/licoes";
import {
  CabecalhoCartao,
  Campo,
  Cartao,
  Chave,
  DESTAQUE,
  Faixa,
  ItemAviso,
  Linha,
  MiniBotao,
  Moldura,
  Pilha,
  Pilula,
  Poderes,
  Sub,
  Texto,
} from "@/core/components/ajuda/ilustracaoPecas";

/**
 * Ilustrações das lições de cada elo (Escala, Biblioteca, Eventos, Reservas, Comunicados e
 * Visitantes). Desenhos estáticos: os rótulos de botões e avisos são os das telas reais; os
 * nomes e exemplos (Carla, Bruno, "Culto de jovens") são inventados. As que dependem de a pessoa
 * gerenciar o elo ou só liderar recebem o `papel`.
 */

/** "Pedido pendente" com Aprovar e Recusar, como nas listas de Empréstimos, Eventos e Reservas. */
function CartaoPedido({
  cabecalho,
  nome,
  apoio,
  destacarAprovar = false,
}: {
  cabecalho: string;
  nome: string;
  apoio: string;
  destacarAprovar?: boolean;
}) {
  return (
    <Cartao>
      <CabecalhoCartao titulo={cabecalho} />
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <span className="block text-sm font-bold text-text">{nome}</span>
          <Sub>{apoio}</Sub>
          <span className="mt-1 inline-block">
            <Pilula tom="espera">Pendente</Pilula>
          </span>
        </div>
        <div className="flex shrink-0 gap-1.5">
          <MiniBotao rotulo="Aprovar" destacado={destacarAprovar} />
          <MiniBotao rotulo="Recusar" secundario />
        </div>
      </div>
    </Cartao>
  );
}

export function IlustracaoElo({ id, papel }: { id: IlustracaoEloId; papel: PapelAjuda }) {
  const geraEventos = gerenciaElo(papel, "eventos");
  const geraReservas = gerenciaElo(papel, "reservas");
  const geraVisitantes = gerenciaElo(papel, "visitantes");

  switch (id) {
    /* ---------- Escala ---------- */
    case "poderes-escala":
      return (
        <Moldura descricao="Quatro coisas que você pode fazer na Escala: criar escalas, escalar pessoas, pedir confirmação e ajustar ou cancelar.">
          <Poderes
            itens={[
              { icone: CalendarCheck, titulo: "Criar escalas", apoio: "Escolha o ministério, o dia e o horário." },
              { icone: Users, titulo: "Escalar pessoas", apoio: "Cada pessoa entra com a sua função." },
              { icone: ShieldCheck, titulo: "Pedir confirmação", apoio: "Cada pessoa confirma a própria presença." },
              { icone: Settings, titulo: "Ajustar e cancelar", apoio: "Mude a função, remova alguém ou cancele." },
            ]}
          />
        </Moldura>
      );

    case "escala-nova":
      return (
        <Moldura descricao="Tela Nova escala com ministério, data e horário. A opção Exigir confirmação dos participantes está ligada e em destaque. No fim, o botão Criar escala.">
          <Cartao>
            <CabecalhoCartao titulo="Nova escala" />
            <Campo rotulo="Ministério" valor="Biblioteca" />
            <Campo rotulo="Data e horário" valor="Domingo, 19h" />
            <Chave
              titulo="Exigir confirmação dos participantes"
              apoio="Cada participante precisa confirmar a própria presença."
              ligada
              destacada
            />
            <div className="mt-3">
              <MiniBotao rotulo="Criar escala" cheio />
            </div>
          </Cartao>
        </Moldura>
      );

    case "escala-equipe":
      return (
        <Moldura descricao="Equipe da escala. O botão Escalar participantes está em destaque. Carla, na função Voz, confirmou. Bruno, na função Guitarra, está pendente. Abaixo, o aviso Conflito de horário sobre Bruno.">
          <Cartao>
            <CabecalhoCartao
              titulo="Equipe"
              direita={<MiniBotao rotulo="Escalar participantes" destacado />}
            />
            <Linha nome="Carla" apoio="Voz" direita={<Pilula tom="ok">Confirmado</Pilula>} />
            <Linha nome="Bruno" apoio="Guitarra" direita={<Pilula tom="espera">Pendente</Pilula>} />
            <Faixa icone={CircleAlert} tom="alerta">
              <strong>Conflito de horário:</strong> Bruno já está escalado(a) em outra escala neste
              mesmo horário. Você ainda pode escalar — é só um aviso.
            </Faixa>
          </Cartao>
        </Moldura>
      );

    case "escala-menu":
      return (
        <Moldura descricao="Participante Bruno com o menu de três pontos aberto, mostrando Alterar função, Registrar resposta e Remover da escala.">
          <Cartao>
            <Linha
              nome="Bruno"
              apoio="Guitarra · Pendente"
              direita={
                <span
                  className={`grid h-8 w-8 place-items-center rounded-lg border border-border bg-bg text-base font-black text-text ${DESTAQUE}`}
                >
                  ⋯
                </span>
              }
            />
            <div className="mt-2 overflow-hidden rounded-lg border border-border bg-bg text-xs font-semibold text-text">
              <div className="px-3 py-2">Alterar função</div>
              <div className="border-t border-border px-3 py-2">Registrar resposta</div>
              <div className="border-t border-border px-3 py-2 text-danger">Remover da escala</div>
            </div>
          </Cartao>
        </Moldura>
      );

    /* ---------- Biblioteca ---------- */
    case "poderes-biblioteca":
      return (
        <Moldura descricao="Quatro coisas que você pode fazer na Biblioteca: cadastrar livros, organizar por categorias, aprovar empréstimos e registrar devoluções.">
          <Poderes
            itens={[
              { icone: BookOpen, titulo: "Cadastrar livros", apoio: "Cada livro tem um número de cadastro." },
              { icone: BookMarked, titulo: "Organizar por categorias", apoio: "Ajuda a achar o livro no acervo." },
              { icone: CircleCheck, titulo: "Aprovar empréstimos", apoio: "Aprove ou recuse cada pedido." },
              { icone: History, titulo: "Registrar devoluções", apoio: "Encerra o empréstimo." },
            ]}
          />
        </Moldura>
      );

    case "bib-livro":
      return (
        <Moldura descricao="Acervo de livros com o botão + Novo livro em destaque. Um livro de exemplo aparece com o número de cadastro 12. Abaixo, o botão + Nova categoria.">
          <Cartao>
            <CabecalhoCartao
              titulo="Acervo de livros"
              direita={<MiniBotao rotulo="+ Novo livro" destacado />}
            />
            <div className="flex items-center gap-3">
              <span className="w-12 shrink-0 rounded-lg border border-border bg-surface-2 py-1 text-center">
                <span className="block text-[9px] font-extrabold tracking-wider text-muted">Nº</span>
                <span className="block text-base font-black text-text">12</span>
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-bold text-text">Livro de exemplo</span>
                <Sub>Categoria: Devocional</Sub>
              </span>
            </div>
            <div className="mt-3">
              <MiniBotao rotulo="+ Nova categoria" secundario />
            </div>
          </Cartao>
        </Moldura>
      );

    case "bib-pedido":
      return (
        <Moldura descricao="Lista de Empréstimos. Pedido de Carla para o Livro de exemplo, pendente, com o botão Aprovar em destaque e o botão Recusar.">
          <CartaoPedido
            cabecalho="Empréstimos"
            nome="Livro de exemplo"
            apoio="Pedido de Carla"
            destacarAprovar
          />
        </Moldura>
      );

    case "bib-devolucao":
      return (
        <Moldura descricao="Empréstimo em andamento do Livro de exemplo, com Carla. O botão Registrar devolução está em destaque. Abaixo, o aviso Livro devolvido que a equipe recebe.">
          <Pilha>
            <Cartao>
              <Linha
                nome="Livro de exemplo"
                apoio="Com Carla"
                direita={<MiniBotao rotulo="Registrar devolução" destacado />}
              />
            </Cartao>
            <ItemAviso
              icone={BookOpen}
              texto="Livro devolvido"
              quando="O livro “Livro de exemplo” foi devolvido por Carla. Registrado por Bruno."
            />
          </Pilha>
        </Moldura>
      );

    /* ---------- Eventos ---------- */
    case "poderes-eventos":
      return (
        <Moldura
          descricao={
            geraEventos
              ? "Quatro coisas que você pode fazer em Eventos: criar e editar, aprovar pedidos, reservar o ambiente e cancelar."
              : "Três coisas que você pode fazer em Eventos: pedir um evento, esperar a aprovação e cancelar o evento."
          }
        >
          <Poderes
            itens={
              geraEventos
                ? [
                    { icone: CalendarDays, titulo: "Criar e editar eventos", apoio: "Públicos ou privados." },
                    { icone: CircleCheck, titulo: "Aprovar pedidos", apoio: "Dos eventos que os ministérios pedem." },
                    { icone: DoorOpen, titulo: "Reservar o ambiente", apoio: "O Elos reserva e confere conflitos." },
                    { icone: Ban, titulo: "Cancelar eventos", apoio: "Sempre com o motivo." },
                  ]
                : [
                    { icone: CalendarDays, titulo: "Pedir um evento", apoio: "Para o seu ministério." },
                    { icone: Hourglass, titulo: "Esperar a aprovação", apoio: "Quem gerencia Eventos aprova ou recusa." },
                    { icone: Ban, titulo: "Cancelar o evento", apoio: "Sempre com o motivo." },
                  ]
            }
          />
        </Moldura>
      );

    case "ev-novo":
      return (
        <Moldura
          descricao={`Lista de Eventos com o botão + Novo evento em destaque e o evento Culto de jovens pendente.${
            geraEventos ? "" : " Abaixo, a mensagem: Evento enviado para aprovação de quem gerencia Eventos."
          }`}
        >
          <Pilha>
            <Cartao>
              <CabecalhoCartao
                titulo="Eventos"
                direita={<MiniBotao rotulo="+ Novo evento" destacado />}
              />
              <Linha
                nome="Culto de jovens"
                apoio="Sábado, 19h"
                direita={<Pilula tom="espera">Pendente</Pilula>}
              />
            </Cartao>
            {!geraEventos && (
              <Faixa tom="ok">Evento enviado para aprovação de quem gerencia Eventos.</Faixa>
            )}
          </Pilha>
        </Moldura>
      );

    case "ev-aprovar":
      return (
        <Moldura descricao="Evento Culto de jovens pendente, com o botão Aprovar em destaque e o botão Recusar. Abaixo, o campo Motivo da recusa.">
          <Pilha>
            <CartaoPedido
              cabecalho="Eventos"
              nome="Culto de jovens"
              apoio="Pedido do ministério Jovens"
              destacarAprovar
            />
            <Cartao>
              <CabecalhoCartao titulo="Recusar evento" />
              <Campo rotulo="Motivo da recusa" valor="Ex.: o salão já está ocupado" />
            </Cartao>
          </Pilha>
        </Moldura>
      );

    case "ev-ambiente":
      return (
        <Moldura descricao="Evento Culto de jovens aprovado, no Salão. Aviso: ambiente reservado automaticamente, sem conflito. Botões Editar evento e Cancelar evento, este em destaque.">
          <Cartao>
            <Linha
              nome="Culto de jovens"
              apoio="Sábado, 19h · Salão"
              direita={<Pilula tom="ok">Aprovado</Pilula>}
            />
            <Faixa icone={DoorOpen} tom="info">
              Ambiente <strong>Salão</strong> reservado automaticamente, sem conflito.
            </Faixa>
            <div className="mt-3 flex gap-2">
              <MiniBotao rotulo="Editar evento" secundario />
              <MiniBotao rotulo="Cancelar evento" secundario destacado />
            </div>
          </Cartao>
        </Moldura>
      );

    /* ---------- Reservas ---------- */
    case "poderes-reservas":
      return (
        <Moldura
          descricao={
            geraReservas
              ? "Quatro coisas que você pode fazer em Reservas: aprovar ou recusar pedidos, cuidar dos ambientes, pedir reservas e editar ou cancelar."
              : "Três coisas que você pode fazer em Reservas: pedir uma reserva, esperar a aprovação e cancelar."
          }
        >
          <Poderes
            itens={
              geraReservas
                ? [
                    { icone: DoorOpen, titulo: "Aprovar ou recusar pedidos", apoio: "Os ministérios pedem salas e ambientes." },
                    { icone: Settings, titulo: "Cuidar dos ambientes", apoio: "Cadastre as salas e os espaços." },
                    { icone: CalendarRange, titulo: "Pedir reservas", apoio: "Para o seu ministério também." },
                    { icone: Ban, titulo: "Editar e cancelar", apoio: "Sempre com o motivo." },
                  ]
                : [
                    { icone: DoorOpen, titulo: "Pedir uma reserva", apoio: "Escolha o ambiente, o dia e o horário." },
                    { icone: Hourglass, titulo: "Esperar a aprovação", apoio: "Quem gerencia Reservas decide." },
                    { icone: Ban, titulo: "Cancelar a reserva", apoio: "Se você não precisar mais." },
                  ]
            }
          />
        </Moldura>
      );

    case "res-nova":
      return (
        <Moldura
          descricao={`Lista de Reservas com o botão + Nova reserva em destaque e a reserva da Sala 2 pendente.${
            geraReservas ? "" : " Abaixo, a mensagem: Solicitação enviada, aguardando aprovação de quem gerencia Reservas."
          }`}
        >
          <Pilha>
            <Cartao>
              <CabecalhoCartao
                titulo="Reservas"
                direita={<MiniBotao rotulo="+ Nova reserva" destacado />}
              />
              <Linha
                nome="Sala 2"
                apoio="Quarta, 20h"
                direita={<Pilula tom="espera">Pendente</Pilula>}
              />
            </Cartao>
            {!geraReservas && (
              <Faixa tom="ok">Solicitação enviada — aguardando aprovação de quem gerencia Reservas.</Faixa>
            )}
          </Pilha>
        </Moldura>
      );

    case "res-aprovar":
      return (
        <Moldura descricao="Reserva da Sala 2 pendente, com o botão Aprovar em destaque e o botão Recusar.">
          <CartaoPedido
            cabecalho="Reservas"
            nome="Sala 2"
            apoio="Pedido do ministério Jovens · Quarta, 20h"
            destacarAprovar
          />
        </Moldura>
      );

    case "res-ambientes":
      return (
        <Moldura descricao="Lista de Ambientes com o botão + Novo ambiente em destaque. Dois ambientes: Salão e Sala 2.">
          <Cartao>
            <CabecalhoCartao
              titulo="Ambientes"
              direita={<MiniBotao rotulo="+ Novo ambiente" destacado />}
            />
            <Linha nome="Salão" />
            <Linha nome="Sala 2" />
          </Cartao>
        </Moldura>
      );

    /* ---------- Comunicados ---------- */
    case "poderes-comunicados":
      return (
        <Moldura descricao="Quatro coisas que você pode fazer em Comunicados: publicar, editar, arquivar e restaurar, e apagar.">
          <Poderes
            itens={[
              { icone: Megaphone, titulo: "Publicar comunicados", apoio: "Escreva um aviso para a igreja." },
              { icone: Settings, titulo: "Editar", apoio: "Corrija o texto quando precisar." },
              { icone: History, titulo: "Arquivar e restaurar", apoio: "Arquivado, deixa de aparecer para os membros." },
              { icone: Trash2, titulo: "Apagar", apoio: "Apaga para todos, sem volta." },
            ]}
          />
        </Moldura>
      );

    case "com-novo":
      return (
        <Moldura descricao="Tela Novo comunicado com título e mensagem preenchidos. O botão Publicar está em destaque.">
          <Cartao>
            <CabecalhoCartao titulo="Novo comunicado" />
            <Campo rotulo="Título" valor="Culto de domingo mudou de horário" />
            <Campo rotulo="Mensagem" valor="Neste domingo o culto será às 18h." />
            <div className="mt-3">
              <MiniBotao rotulo="Publicar" cheio destacado />
            </div>
          </Cartao>
        </Moldura>
      );

    case "com-prazo":
      return (
        <Moldura descricao="Opção Apagar sozinho em 30 dias, ligada e em destaque.">
          <Cartao>
            <Chave
              titulo="Apagar sozinho em 30 dias"
              apoio="O comunicado some 30 dias depois de publicado."
              ligada
              destacada
            />
            <div className="mt-2">
              <Texto pequeno>Ao editar, a contagem de 30 dias não recomeça.</Texto>
            </div>
          </Cartao>
        </Moldura>
      );

    case "com-acoes":
      return (
        <Moldura descricao="Dois comunicados. O primeiro, publicado, tem os botões Editar e Arquivar, este em destaque. O segundo, arquivado, tem os botões Restaurar e Apagar.">
          <Pilha>
            <Cartao>
              <Linha nome="Culto de domingo mudou de horário" apoio="Publicado por Ana" />
              <div className="mt-2 flex gap-2">
                <MiniBotao rotulo="Editar" secundario />
                <MiniBotao rotulo="Arquivar" secundario destacado />
              </div>
            </Cartao>
            <Cartao>
              <Linha nome="Campanha do agasalho" apoio="Arquivado" />
              <div className="mt-2 flex gap-2">
                <MiniBotao rotulo="Restaurar" secundario />
                <MiniBotao rotulo="Apagar" secundario />
              </div>
            </Cartao>
          </Pilha>
        </Moldura>
      );

    /* ---------- Visitantes ---------- */
    case "poderes-visitantes":
      return (
        <Moldura
          descricao={
            geraVisitantes
              ? "Quatro coisas que você pode fazer em Visitantes: cadastrar e editar, avaliar quem foi informado, registrar visitas e contatos e cuidar da privacidade."
              : "Quatro coisas que você pode fazer em Visitantes: informar um novo visitante, registrar contato, ver o básico e saber o que fica de fora."
          }
        >
          <Poderes
            itens={
              geraVisitantes
                ? [
                    { icone: UserRound, titulo: "Cadastrar e editar visitantes", apoio: "Você vê todos os dados." },
                    { icone: CircleCheck, titulo: "Avaliar quem foi informado", apoio: "Na aba Visitantes pendentes." },
                    { icone: CalendarCheck, titulo: "Registrar visitas e contatos", apoio: "Para cuidar de cada pessoa." },
                    { icone: Lock, titulo: "Cuidar da privacidade", apoio: "Os dados são só para o cuidado." },
                  ]
                : [
                    { icone: UserRound, titulo: "Informar um novo visitante", apoio: "A equipe do elo avalia." },
                    { icone: Clock, titulo: "Registrar contato", apoio: "Outros líderes veem que a pessoa já foi acompanhada." },
                    { icone: Users, titulo: "Ver o básico", apoio: "Nome, idade, telefone e cidade." },
                    { icone: Lock, titulo: "Não ver tudo", apoio: "Crianças e quem frequenta outra igreja ficam de fora." },
                  ]
            }
          />
        </Moldura>
      );

    case "vis-novo":
      return (
        <Moldura descricao="Lista de Visitantes com o botão Novo em destaque. Abaixo, o aviso: Crianças e quem frequenta outra igreja não aparecem aqui.">
          <Cartao>
            <CabecalhoCartao
              titulo="Visitantes"
              direita={<MiniBotao rotulo="Novo" icone={Plus} destacado />}
            />
            <p className="m-0 flex items-start gap-1.5 text-xs text-muted">
              <Lock size={13} className="mt-0.5 shrink-0" />
              <span>
                Crianças e quem frequenta outra igreja não aparecem aqui. Os dados são para o
                cuidado da pessoa na igreja.
              </span>
            </p>
          </Cartao>
        </Moldura>
      );

    case "vis-avaliar":
      return (
        <Moldura descricao="Abas da lista de Visitantes: Visitantes e Visitantes pendentes, com 1 pendente, esta em destaque. Abaixo, Ricardo, informado por Bruno, com o botão Avaliar.">
          <Cartao>
            <div className="mb-2 flex flex-wrap gap-2">
              <MiniBotao rotulo="Visitantes" secundario />
              <MiniBotao rotulo="Visitantes pendentes (1)" destacado />
            </div>
            <Linha
              nome="Ricardo"
              apoio="Informado por Bruno"
              direita={<MiniBotao rotulo="Avaliar" />}
            />
          </Cartao>
        </Moldura>
      );

    case "vis-privacidade":
      return (
        <Moldura descricao="Dois quadros. Ministério responsável: vê todos os dados e cadastra. Líderes de outros ministérios: veem nome, idade, telefone, cidade e os contatos já feitos, sem endereço nem pedido de oração.">
          <div className="grid grid-cols-2 gap-2">
            <Cartao>
              <span className="block text-xs font-extrabold text-text">Ministério responsável</span>
              <ul className="m-0 mt-2 list-disc pl-4 text-[11.5px] leading-snug text-text">
                <li>Vê todos os dados</li>
                <li>Cadastra e edita</li>
              </ul>
            </Cartao>
            <Cartao>
              <span className="block text-xs font-extrabold text-text">Outros líderes</span>
              <ul className="m-0 mt-2 list-disc pl-4 text-[11.5px] leading-snug text-text">
                <li>Nome, idade, telefone e cidade</li>
                <li>Contatos já feitos</li>
                <li>Sem endereço nem pedido de oração</li>
              </ul>
            </Cartao>
          </div>
        </Moldura>
      );
  }
}
